import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, combineLatest, distinctUntilChanged, filter, map, of, startWith, switchMap, take, takeUntil } from 'rxjs';
import { Project } from 'src/app/models/project.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LineSelectionService } from 'src/app/services/line-selection/line-selection.service';
import { ProjectCartService } from 'src/app/services/project-cart/project-cart.service';
import { ProjectService } from 'src/app/services/project/project.service';
import { COMPANY_LOGO_FILE, lineLogoFor } from 'src/app/shared/line-logos';
import { environment } from 'src/environments/enviroment.prod';
import { Crumb, buildBreadcrumbs, productIdFromUrl } from '../breadcrumbs';
import { ShellContextService, SidenavToggleState } from '../shell-context.service';

interface CrumbView extends Crumb {
  /** Hidden below 768px: only the last two crumbs stay visible. */
  narrowHidden: boolean;
  /** The longest label: the one that truncates first when the bar is tight. */
  shrinks: boolean;
}

interface ProjectItem {
  id: number;
  name: string;
  metaLabel: string;
}

const MAX_MENU_PROJECTS = 8;

@Component({
  selector: 'app-shell-top-bar',
  templateUrl: './shell-top-bar.component.html',
  styleUrls: ['./shell-top-bar.component.scss'],
})
export class ShellTopBarComponent implements OnInit, OnDestroy {
  /** Centre of the bar: the selected line's logo on product routes, the company logo elsewhere. */
  centreLogo = environment.imageLink + COMPANY_LOGO_FILE;
  centreLogoAlt = 'Home';
  centreLogoIsLine = false;

  visible = false;
  crumbs: CrumbView[] = [];
  userName = '';
  userInitial = '';
  userMeta = '';
  sidenavToggle: SidenavToggleState = { visible: false, open: false };

  menuOpen = false;
  projects: ProjectItem[] = [];
  projectsLoading = false;
  projectsError = false;

  @ViewChild('accountButton') private accountButton?: ElementRef<HTMLButtonElement>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private auth: AuthService,
    private cart: ProjectCartService,
    private lineSelection: LineSelectionService,
    private projectService: ProjectService,
    private shell: ShellContextService,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    const url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url),
    );
    const productName$ = url$.pipe(
      map(productIdFromUrl),
      distinctUntilChanged(),
      // startWith(null): render the bar immediately; the name fills in when the request returns.
      switchMap(id => (id === null ? of(null) : this.shell.productName$(id).pipe(startWith(null)))),
    );

    combineLatest([
      url$,
      this.auth.user,
      this.cart.project$,
      this.cart.configurations$,
      this.lineSelection.selectedLine$,
      productName$,
      this.shell.tabName$,
    ]).pipe(takeUntil(this.destroy$)).subscribe(([url, user, project, configurations, line, productName, tabName]) => {
      const path = url.split(/[?#]/)[0];
      this.visible = !!user && !path.startsWith('/login') && !path.startsWith('/pdf');
      if (!this.visible) { this.menuOpen = false; }

      this.userName = user?.name ?? '';
      this.userInitial = (user?.name ?? '?').trim().charAt(0).toUpperCase();
      this.userMeta = user ? [user.type, user.TRDR ? `TRDR ${user.TRDR}` : null].filter(Boolean).join(' · ') : '';

      const lineLogo = path.startsWith('/mode') || path.startsWith('/lines') ? null : lineLogoFor(line);
      this.centreLogoIsLine = lineLogo !== null;
      this.centreLogo = lineLogo ?? environment.imageLink + COMPANY_LOGO_FILE;
      this.centreLogoAlt = lineLogo ? `${line} line` : 'Home';

      const crumbs = buildBreadcrumbs({
        url,
        userName: user?.name ?? null,
        mode: project?.mode ?? null,
        projectName: project?.name ?? null,
        hasConfigurations: configurations.length > 0,
        line,
        roomLabel: this.cart.currentRoomLabel,
        productName,
        tabName,
      });
      const longest = crumbs.reduce((best, crumb, index) => (crumb.label.length > crumbs[best].label.length ? index : best), 0);
      this.crumbs = crumbs.map((crumb, index) => ({ ...crumb, narrowHidden: index < crumbs.length - 2, shrinks: index === longest }));
    });

    this.shell.sidenavToggle$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.sidenavToggle = state;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByCrumb(index: number, crumb: CrumbView): string {
    return `${index}-${crumb.label}`;
  }

  trackByProject(_: number, project: ProjectItem): number {
    return project.id;
  }

  onNavigate(event: Event, url: string): void {
    event.preventDefault();
    this.menuOpen = false;
    this.shell.navigate(url);
  }

  onToggleSidenav(): void {
    this.shell.requestSidenavToggle();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) { this.loadProjects(); }
  }

  onResume(item: ProjectItem): void {
    this.menuOpen = false;
    // Switching project discards an unsaved single configuration: let the
    // configurator ask first, then the user picks the project on /mode.
    if (this.shell.wouldBlockLeave()) {
      this.shell.navigate('/mode');
      return;
    }
    this.cart.resumeProject(item.id).pipe(take(1)).subscribe({
      next: resumed => this.router.navigate([(resumed.configurations?.length ?? 0) > 0 ? '/cart' : '/lines']),
      error: () => this.router.navigate(['/mode']),
    });
  }

  onAllProjects(): void {
    this.menuOpen = false;
    this.shell.navigate('/mode');
  }

  onLogout(): void {
    this.menuOpen = false;
    this.auth.logout();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.menuOpen) { return; }
    this.menuOpen = false;
    this.accountButton?.nativeElement.focus();
  }

  private loadProjects(): void {
    this.projectsLoading = true;
    this.projectsError = false;
    this.projectService.listResumable().pipe(take(1)).subscribe({
      next: projects => {
        this.projects = projects.slice(0, MAX_MENU_PROJECTS).map(toProjectItem);
        this.projectsLoading = false;
      },
      error: () => {
        this.projectsLoading = false;
        this.projectsError = true;
      },
    });
  }
}

function toProjectItem(project: Project): ProjectItem {
  const count = project.configurations_count ?? project.configurations?.length ?? 0;
  const countLabel = `${count} ${count === 1 ? 'configuration' : 'configurations'}`;
  return {
    id: project.id,
    name: project.name ?? 'Untitled project',
    metaLabel: [project.production_code, countLabel].filter(Boolean).join(' · '),
  };
}
